import * as types from './actionTypes';
import * as authTypes from '../user/actionTypes';

import OntologyClient from '../../focus-client/ontology-client';

import { createQuery } from '../../utils/queryUtil';
import { setStoredPreference } from '../../utils/preferenceUtil';

const ONTOLOGY_CACHE_VERSION = 'v1';
const ONTOLOGY_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const ONTOLOGY_CACHE_PREFIX = `focus-ontology-hierarchy-${ONTOLOGY_CACHE_VERSION}`;

const getOntologyCacheKey = language =>
  `${ONTOLOGY_CACHE_PREFIX}:${language || 'default'}`;

const readHierarchyCache = language => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getOntologyCacheKey(language));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    const timestamp = parsed && parsed.timestamp ? Number(parsed.timestamp) : 0;
    const concepts = parsed && parsed.payload ? parsed.payload.concepts : null;
    if (
      !parsed ||
      !parsed.payload ||
      !Array.isArray(concepts) ||
      concepts.length === 0 ||
      !Number.isFinite(timestamp)
    ) {
      return null;
    }

    return {
      payload: parsed.payload,
      fresh: Date.now() - timestamp < ONTOLOGY_CACHE_TTL_MS
    };
  } catch (error) {
    return null;
  }
};

const writeHierarchyCache = (language, payload) => {
  if (typeof window === 'undefined' || !window.localStorage || !payload) {
    return;
  }
  if (!Array.isArray(payload.concepts) || payload.concepts.length === 0) {
    return;
  }

  try {
    const value = JSON.stringify({
      timestamp: Date.now(),
      payload
    });
    window.localStorage.setItem(getOntologyCacheKey(language), value);
  } catch (error) {
    // Ignore cache write failures (quota/private mode), keep app functional.
  }
};

const getLanguages = () => {
  var config = {
    baseURI: document.baseURI
  };

  return dispatch => {
    let ontology = OntologyClient(config);

    return ontology.languages().then(response => {
      if (!response.isError) {
        dispatch(setLanguages(response));
      } else if (response.error.status === 401) {
        dispatch(localLogout());
      }
    });
  };
};

const getLabels = () => {
  var config = {
    baseURI: document.baseURI
  };

  return dispatch => {
    let ontology = OntologyClient(config);

    return ontology.labels().then(response => {
      if (!response.isError) {
        dispatch(setLabels(response));
      } else if (response.error.status === 401) {
        dispatch(localLogout());
      }
    });
  };
};

const getFilterTreeList = (values, optionalArgs = {}) => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    let ontology = OntologyClient(config);
    const language = getState().ontology.selectedLanguage || '';
    const cache = readHierarchyCache(language);
    const forceRefresh = optionalArgs.forceRefresh === true;
    const offlineOnly = optionalArgs.offlineOnly === true;

    if (cache && !forceRefresh) {
      dispatch(setFilterTreeList(cache.payload));
      if (cache.fresh || offlineOnly) {
        return Promise.resolve(cache.payload);
      }
    } else if (offlineOnly) {
      return Promise.resolve(null);
    }

    return ontology.hierarchy(language).then(response => {
      if (!response.isError) {
        dispatch(setFilterTreeList(response));
        writeHierarchyCache(language, response);
      } else if (response.error.status === 401) {
        dispatch(localLogout());
      }
      return response;
    });
  };
};

const getConceptsCount = () => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    let ontology = OntologyClient(config);
    let coordinates = {
      north: getState().query.north,
      east: getState().query.east,
      south: getState().query.south,
      west: getState().query.west
    };

    const query = createQuery(
      false,
      false,
      true,
      true,
      true,
      true,
      coordinates,
      getState().query.areas,
      getState().query.concepts,
      getState().query.mode,
      getState().query.dataSources,
      getState().query.agents
    );
    if (getState().query.searchQuery !== '')
      query.withQueryText(getState().query.searchQuery);
    if (getState().query.start !== null && getState().query.end !== null)
      query.withDateTimeRange(getState().query.end, getState().query.start);

    delete query.options;
    return ontology.counts(query, 'concepts-count').then(response => {
      if (!response.isError) {
        dispatch(setConceptsCount(response));
      } else if (response.error.status === 401) {
        dispatch(localLogout());
      }
    });
  };
};

const localLogout = () => ({
  type: authTypes.LOCAL_LOGOUT
});

const setLanguages = languages => ({
  type: types.SET_LANGUAGES,
  payload: languages
});

const setSelectedLanguage = selectedLanguage => ({
  type: types.SET_SELECTED_LANGUAGE,
  payload: selectedLanguage
});

const setUpdateInterval = interval => ({
  type: types.SET_UPDATE_INTERVAL,
  payload: interval
});

const setAlertTimeline = value => ({
  type: types.SET_ALERT_TIMELINE,
  payload: value
});

const setShowAgentNames = value => ({
  type: types.SET_SHOW_AGENT_NAMES,
  payload: value
});

const setHideOldAgents = value => ({
  type: types.SET_HIDE_OLD_AGENTS,
  payload: value
});

const setShowInactiveAgents = value => ({
  type: types.SET_SHOW_INACTIVE_AGENTS,
  payload: value
});

const setShowObservationClusters = value => ({
  type: types.SET_SHOW_OBSERVATION_CLUSTERS,
  payload: value
});

const setObservationClusterThreshold = value => ({
  type: types.SET_OBSERVATION_CLUSTER_THRESHOLD,
  payload: value
});

const setShowObservationHeatmap = value => ({
  type: types.SET_SHOW_OBSERVATION_HEATMAP,
  payload: value
});

const setShowTrackHeatmap = value => ({
  type: types.SET_SHOW_TRACK_HEATMAP,
  payload: value
});

const setShowTrackFullStandard = value => ({
  type: types.SET_SHOW_TRACK_FULL_STANDARD,
  payload: value
});

const setShowTrackFullColour = value => ({
  type: types.SET_SHOW_TRACK_FULL_COLOUR,
  payload: value
});

const setObservationHeatmapResolution = value => ({
  type: types.SET_OBSERVATION_HEATMAP_RESOLUTION,
  payload: value
});

const setTrackHeatmapResolution = value => ({
  type: types.SET_TRACK_HEATMAP_RESOLUTION,
  payload: value
});

const setObservationHeatmapOpacity = value => {
  setStoredPreference('observationHeatmapOpacity', value);
  return {
    type: types.SET_OBSERVATION_HEATMAP_OPACITY,
    payload: value
  };
};

const setTrackHeatmapOpacity = value => {
  setStoredPreference('trackHeatmapOpacity', value);
  return {
    type: types.SET_TRACK_HEATMAP_OPACITY,
    payload: value
  };
};

const setTrackColor = value => ({
  type: types.SET_TRACK_COLOR,
  payload: value
});

const setShowGrid = value => ({
  type: types.SET_SHOW_GRID,
  payload: value
});

const setShowCoordinates = value => ({
  type: types.SET_SHOW_COORDINATES,
  payload: value
});

const setFilterTreeList = ontology => ({
  type: types.SET_ONTOLOGY_SUCCESS,
  payload: { ontology }
});

const setLabels = labels => ({
  type: types.SET_LABELS_SUCCESS,
  payload: { labels }
});

const setConceptsCount = count => ({
  type: types.SET_CONCEPTS_COUNT,
  payload: count
});

const clearConceptsCount = () =>
  setConceptsCount({
    'values-response': {
      'distinct-value': []
    }
  });

export default {
  getLanguages,
  getLabels,
  getFilterTreeList,
  getConceptsCount,
  clearConceptsCount,
  setSelectedLanguage,
  setUpdateInterval,
  setAlertTimeline,
  setShowAgentNames,
  setHideOldAgents,
  setShowInactiveAgents,
  setShowObservationClusters,
  setObservationClusterThreshold,
  setShowObservationHeatmap,
  setShowTrackHeatmap,
  setShowTrackFullStandard,
  setShowTrackFullColour,
  setObservationHeatmapResolution,
  setTrackHeatmapResolution,
  setObservationHeatmapOpacity,
  setTrackHeatmapOpacity,
  setTrackColor,
  setShowGrid,
  setShowCoordinates
};
